import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Input, Segmented, Select, Spin, message } from "antd";
import { AlertTriangle, CheckCircle2, Cloud, HardDrive, RefreshCw, ShieldCheck, XCircle } from "lucide-react";
import { AN_COLORS, AN_MONO } from "../../../design-system/tokens";
import { fmtDate } from "../../../design-system/tableUi";
import { InlineValue, Note } from "../ssh-proxy/sshProxyUi";
import {
  SETTINGS_TOKENS,
  SettingsField,
  SettingsPage,
  SettingsRow,
  SettingsSection,
  SettingsStatus,
} from "../../../components/settings/settingsUi";
import { isAdminUser } from "../../../utils/helpers";
import {
  getRecordingStorage,
  recordingStorageError,
  retryHeldRecordings,
  setRecordingStorage,
  testRecordingStorage,
} from "../../../service/recordingStorageService";

// Settings → Recording Storage: where SSH session recordings are kept.
//
// Backend: internal/pam/handler/recordingstorage + internal/pam/recordingstore.
// Ingest always writes to the control plane's volume first; with S3 or GCS
// configured, an offloader copies each recording to the bucket, checks it
// landed, and only then removes the local copy. A recording whose local copy is
// missing or altered is "held" (never uploaded) until someone releases it.
//
// Validation mirrors recordingstore.Config.Validate / Credentials.ValidFor so
// the form catches what the server would refuse with a 400.

const PROVIDERS = [
  { value: "local", label: "Local volume" },
  { value: "s3", label: "Amazon S3 / S3-compatible" },
  { value: "gcs", label: "Google Cloud Storage" },
];
const PROVIDER_NAME = { local: "Local volume", s3: "Amazon S3", gcs: "Google Cloud Storage" };

const SSE_OPTIONS = [
  { value: "", label: "Bucket default" },
  { value: "AES256", label: "SSE-S3 (AES256)" },
  { value: "aws:kms", label: "SSE-KMS (aws:kms)" },
];

const BUCKET_RE = /^[a-z0-9][a-z0-9._-]{1,220}[a-z0-9]$/;
const REGION_RE = /^[a-z0-9-]{1,40}$/;
const PREFIX_PART_RE = /^[A-Za-z0-9._-]+$/;

const EMPTY_SECRETS = { accessKeyId: "", secretAccessKey: "", sessionToken: "", serviceAccountJson: "" };

const draftFrom = (cfg = {}) => ({
  provider: cfg.provider || "local",
  bucket: cfg.bucket || "",
  region: cfg.region || "",
  endpoint: cfg.endpoint || "",
  prefix: cfg.prefix || "",
  pathStyle: !!cfg.pathStyle,
  sse: cfg.sse || "",
  kmsKeyId: cfg.kmsKeyId || "",
  credentialMode: cfg.credentialMode || "ambient",
  ...EMPTY_SECRETS,
  backfill: false,
});

// What the server compares after Normalise(): trimmed, prefix without slashes,
// endpoint without a trailing slash, and only the fields the provider uses.
const normalised = (d) => {
  const provider = d.provider || "local";
  if (provider === "local") return { provider: "local", credentialMode: "ambient" };
  const base = {
    provider,
    bucket: d.bucket.trim(),
    endpoint: d.endpoint.trim().replace(/\/+$/, ""),
    prefix: d.prefix.trim().replace(/^\/+|\/+$/g, ""),
    credentialMode: d.credentialMode || "ambient",
  };
  if (provider === "s3") {
    return {
      ...base,
      region: d.region.trim(),
      pathStyle: !!d.pathStyle,
      sse: d.sse,
      kmsKeyId: d.sse === "aws:kms" ? d.kmsKeyId.trim() : "",
    };
  }
  return { ...base, region: "", pathStyle: false, sse: "", kmsKeyId: d.kmsKeyId.trim() };
};

const typedSecrets = (d) =>
  d.provider === "s3"
    ? !!(d.accessKeyId || d.secretAccessKey || d.sessionToken)
    : d.provider === "gcs"
      ? !!d.serviceAccountJson.trim()
      : false;

function validate(d, saved) {
  const errs = {};
  if (d.provider === "local") return errs;
  const n = normalised(d);
  if (!BUCKET_RE.test(n.bucket) || n.bucket.includes("..")) {
    errs.bucket = "3–222 characters: lowercase letters, digits, dots, hyphens, underscores; must start and end with a letter or digit.";
  }
  if (n.region && !REGION_RE.test(n.region)) errs.region = "Lowercase letters, digits and hyphens, e.g. us-east-1.";
  if (n.endpoint) {
    let ok = false;
    try {
      const u = new URL(n.endpoint);
      ok = (u.protocol === "https:" || u.protocol === "http:") && !!u.host && !u.username && !u.password &&
        (u.pathname === "" || u.pathname === "/") && !u.search && !u.hash && !/[?#]/.test(n.endpoint);
    } catch (_) {
      ok = false;
    }
    if (!ok) errs.endpoint = "A plain http(s)://host[:port] URL, with no path.";
  }
  if (n.prefix) {
    const bad = n.prefix.split("/").some((p) => !PREFIX_PART_RE.test(p) || p === "." || p === "..");
    if (bad) errs.prefix = "Path segments of letters, digits, dots, hyphens and underscores only.";
    else if (n.prefix.length > 200) errs.prefix = "At most 200 characters.";
  }
  if (n.kmsKeyId.length > 512) errs.kmsKeyId = "At most 512 characters.";

  if (n.credentialMode === "static") {
    const storedUsable = !!saved?.hasCredential && saved?.provider === d.provider && saved?.credentialMode === "static";
    if (!typedSecrets(d)) {
      if (!storedUsable) errs.credentials = "Enter a credential — none is stored for this provider.";
    } else if (d.provider === "s3") {
      if (!d.accessKeyId.trim()) errs.accessKeyId = "Required with a secret access key.";
      if (!d.secretAccessKey) errs.secretAccessKey = "Required with an access key ID.";
    } else if (d.provider === "gcs") {
      try {
        const sa = JSON.parse(d.serviceAccountJson);
        if (!sa || sa.type !== "service_account" || !sa.client_email || !sa.private_key) {
          errs.serviceAccountJson = "Must be a service account key file (type \"service_account\" with client_email and private_key).";
        }
      } catch (_) {
        errs.serviceAccountJson = "Not valid JSON.";
      }
    }
  }
  return errs;
}

// The request body for /recordingStorage/set. Credentials go only with
// credentialMode static and only when typed: an empty (null) credential keeps
// the stored one, which the server allows only when the provider is unchanged.
function buildBody(d) {
  const n = normalised(d);
  if (n.provider === "local") {
    return { provider: "local", credentialMode: "ambient", credentials: null, backfill: false };
  }
  let credentials = null;
  if (n.credentialMode === "static" && typedSecrets(d)) {
    credentials = d.provider === "s3"
      ? {
          accessKeyId: d.accessKeyId.trim(),
          secretAccessKey: d.secretAccessKey,
          ...(d.sessionToken.trim() ? { sessionToken: d.sessionToken.trim() } : {}),
        }
      : { serviceAccountJson: d.serviceAccountJson.trim() };
  }
  return { ...n, credentials, backfill: !!d.backfill };
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// Counts from readCounts(): local = rows whose copy is still on the volume
// (this includes pending and held ones); pending = queued for upload; failing =
// pending rows that have already failed at least once (a subset of pending);
// held = refused for upload (missing or altered local copy); remote = in S3/GCS.
function statusParts(provider, rec) {
  if (!rec) return [];
  const parts = [];
  if (provider === "local") {
    parts.push({ text: `${rec.local} on the local volume` });
    if (rec.remote > 0) parts.push({ text: `${rec.remote} in a bucket from an earlier configuration` });
    if (rec.pending > 0) parts.push({ text: `${rec.pending} waiting to upload`, tone: "warn" });
  } else {
    parts.push({ text: `${rec.remote} in bucket` });
    parts.push({ text: `${rec.pending} waiting`, tone: rec.pending > 0 ? "warn" : undefined });
    if (rec.failing > 0) parts.push({ text: `${rec.failing} retrying after an error`, tone: "danger" });
    const localOnly = Math.max(0, rec.local - rec.pending - rec.held);
    if (localOnly > 0) parts.push({ text: `${localOnly} kept locally (not queued)` });
  }
  parts.push({ text: `${rec.held} held`, tone: rec.held > 0 ? "danger" : undefined });
  return parts;
}

// A padded block inside a settings card, for notes and lines that are not fields.
function CardBlock({ children, last = false, style }) {
  return (
    <div style={{ padding: SETTINGS_TOKENS.rowPad, borderBottom: last ? "none" : `1px solid ${SETTINGS_TOKENS.hairline}`, ...style }}>
      {children}
    </div>
  );
}

function StoredTag() {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 500, color: AN_COLORS.success, background: "var(--an-successBg)", border: "1px solid var(--an-successBd)", borderRadius: 999, padding: "1px 8px" }}>
      <ShieldCheck size={12} /> Stored
    </span>
  );
}

function TestResultNote({ test, prefix }) {
  if (!test) return null;
  if (!test.ok) {
    return (
      <Note tone="danger" icon={<XCircle size={15} />}>
        <strong>{prefix || "Test failed."}</strong> {test.error}
      </Note>
    );
  }
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <Note tone="ok" icon={<CheckCircle2 size={15} />}>
        <strong>{prefix || "Test passed."}</strong> A test object was written and read back
        {test.deleteAllowed ? " and deleted." : "."}
      </Note>
      {test.warning && (
        <Note tone="warn" icon={<AlertTriangle size={15} />}>
          <strong>Note:</strong> {test.warning}
        </Note>
      )}
    </div>
  );
}

export default function RecordingStorage() {
  const admin = isAdminUser();
  const [msg, msgCtx] = message.useMessage();

  const [loading, setLoading] = useState(admin);
  const [loadError, setLoadError] = useState("");
  const [saved, setSaved] = useState(null);
  const [recordings, setRecordings] = useState(null);
  const [draft, setDraft] = useState(draftFrom());
  const [touched, setTouched] = useState(false);

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [saveError, setSaveError] = useState(null); // { error, test }
  const [result, setResult] = useState(null); // { kind: "save"|"test", test, backfilled, message }

  const load = useCallback((resetDraft = true) => {
    setLoading(true);
    setLoadError("");
    return getRecordingStorage()
      .then((data) => {
        const cfg = data?.config || { provider: "local", credentialMode: "ambient" };
        setSaved(cfg);
        setRecordings(data?.recordings || null);
        if (resetDraft) {
          setDraft(draftFrom(cfg));
          setTouched(false);
        }
      })
      .catch((err) => setLoadError(recordingStorageError(err, "Could not read the recording storage configuration")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (admin) load();
  }, [admin, load]);

  const set = (patch) => {
    setDraft((d) => ({ ...d, ...patch }));
    setTouched(true);
    setSaveError(null);
  };

  const errors = useMemo(() => (touched ? validate(draft, saved) : {}), [draft, saved, touched]);
  const allErrors = useMemo(() => validate(draft, saved), [draft, saved]);

  const dirty = useMemo(() => {
    if (!saved) return false;
    const a = normalised(draft);
    const b = normalised(draftFrom(saved));
    return JSON.stringify(a) !== JSON.stringify(b) || typedSecrets(draft) || (draft.provider !== "local" && draft.backfill);
  }, [draft, saved]);

  const providerChanged = saved && draft.provider !== (saved.provider || "local");
  const storedUsable = !!saved?.hasCredential && saved?.provider === draft.provider && saved?.credentialMode === "static";

  const save = () => {
    setTouched(true);
    if (Object.keys(allErrors).length) return;
    setSaving(true);
    setSaveError(null);
    setResult(null);
    setRecordingStorage(buildBody(draft))
      .then((data) => {
        setResult({ kind: "save", test: data?.test || null, backfilled: data?.backfilled || 0 });
        msg.success("Recording storage saved");
        return load(true);
      })
      .catch((err) => {
        setSaveError({
          error: recordingStorageError(err, "Could not save the configuration"),
          test: err?.response?.data?.test || null,
          status: err?.response?.status,
        });
      })
      .finally(() => setSaving(false));
  };

  const runTest = () => {
    setTesting(true);
    setResult(null);
    testRecordingStorage()
      .then((data) => {
        setResult({ kind: "test", test: data?.test || null, message: data?.message });
        return load(false);
      })
      .catch((err) => msg.error(recordingStorageError(err, "Could not run the test")))
      .finally(() => setTesting(false));
  };

  const releaseHeld = () => {
    setReleasing(true);
    retryHeldRecordings()
      .then((data) => {
        const n = data?.released ?? 0;
        msg.success(`${plural(n, "recording")} queued for upload again`);
        return load(false);
      })
      .catch((err) => msg.error(recordingStorageError(err, "Could not release the held recordings")))
      .finally(() => setReleasing(false));
  };

  const description =
    "Where SSH session recordings are kept. Recordings are always written to the control plane first, then copied to your bucket — a bucket that is down delays a recording, it never loses one.";

  if (!admin) {
    return (
      <SettingsPage title="Recording Storage" description={description}>
        <Note tone="info" icon={<ShieldCheck size={15} />}>
          Recording storage can only be viewed and changed by an administrator.
        </Note>
      </SettingsPage>
    );
  }

  const parts = statusParts(saved?.provider || "local", recordings);
  const remote = draft.provider === "s3" || draft.provider === "gcs";
  const savedRemote = saved && (saved.provider === "s3" || saved.provider === "gcs");
  const held = recordings?.held || 0;
  const status = savedRemote && saved.lastTestOk === false
    ? { tone: "danger", label: "Last test failed" }
    : held > 0
      ? { tone: "warn", label: `${held} held` }
      : { tone: "success", label: "In use" };

  const actions = saved ? (
    <>
      {held > 0 && (
        <Button loading={releasing} onClick={releaseHeld} icon={<RefreshCw size={13} style={{ verticalAlign: -2 }} />}>
          Retry held
        </Button>
      )}
      <Button loading={testing} onClick={runTest} disabled={!savedRemote}
        title={savedRemote ? "Test the saved configuration against its bucket" : "Nothing to test for the local volume"}>
        Test
      </Button>
    </>
  ) : null;

  const inputMono = { fontFamily: AN_MONO };

  return (
    <SettingsPage title="Recording Storage" description={description} actions={actions}>
      {msgCtx}

      {loading && !saved ? (
        <div style={{ padding: 60, textAlign: "center" }}><Spin /></div>
      ) : loadError && !saved ? (
        <div style={{ display: "grid", gap: 12 }}>
          <Note tone="danger" icon={<XCircle size={15} />}>{loadError}</Note>
          <div><Button onClick={() => load()}>Try again</Button></div>
        </div>
      ) : (
        <>
          {/* Current state */}
          <SettingsSection title="Status" description="Where recordings are kept now, and how many are where.">
            <SettingsRow
              icon={savedRemote ? <Cloud size={18} /> : <HardDrive size={18} />}
              title={
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {PROVIDER_NAME[saved?.provider] || "Local volume"}
                  {savedRemote && saved.bucket && <InlineValue>{saved.bucket}{saved.prefix ? `/${saved.prefix}` : ""}</InlineValue>}
                </span>
              }
              description={savedRemote ? `Credentials: ${saved.credentialMode === "static" ? "stored key" : "the service's own cloud identity"}` : "Recordings stay on the control plane's own volume."}
              value={<SettingsStatus tone={status.tone}>{status.label}</SettingsStatus>}
            />
            <CardBlock last={!(savedRemote && saved.lastTestAt) && !result}>
              {parts.length > 0 ? (
                <div style={{ fontSize: 13, color: AN_COLORS.ink2, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                  {parts.map((p, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <span style={{ color: AN_COLORS.muted }}>·</span>}
                      {p.tone ? <SettingsStatus tone={p.tone}>{p.text}</SettingsStatus> : <span>{p.text}</span>}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12.5, color: AN_COLORS.muted }}>Recording counts are unavailable.</div>
              )}
              {held > 0 && (
                <div style={{ fontSize: 12, color: AN_COLORS.muted, marginTop: 8, lineHeight: 1.5 }}>
                  Held recordings were not uploaded because their local copy was missing or did not match its checksum.
                  Check the volume, then retry — an altered copy is held again.
                </div>
              )}
            </CardBlock>
            {savedRemote && saved.lastTestAt && (
              <CardBlock last={!result}>
                <div style={{ fontSize: 12.5, color: AN_COLORS.muted, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  Last test {fmtDate(saved.lastTestAt)}
                  {saved.lastTestOk === false
                    ? <SettingsStatus tone="danger">failed{saved.lastTestError ? ` — ${saved.lastTestError}` : ""}</SettingsStatus>
                    : <SettingsStatus tone="success">passed{saved.lastTestError ? ` (note: ${saved.lastTestError})` : ""}</SettingsStatus>}
                </div>
              </CardBlock>
            )}
            {result && (
              <CardBlock last>
                {result.kind === "test" && result.message ? (
                  <Note tone="info">{result.message}</Note>
                ) : (
                  <TestResultNote test={result.test} prefix={result.kind === "save" ? "Saved. Bucket test passed." : undefined} />
                )}
                {result.kind === "save" && result.backfilled > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Note tone="info">{plural(result.backfilled, "existing recording")} queued for upload.</Note>
                  </div>
                )}
              </CardBlock>
            )}
          </SettingsSection>

          {/* Configuration form */}
          <SettingsSection
            title="Storage provider"
            description="A bucket is tested (write, read back, delete) before it is saved; if the test fails nothing is saved."
          >
            <SettingsField label="Provider">
              <Segmented
                block
                options={PROVIDERS}
                value={draft.provider}
                onChange={(v) => set({ provider: v, ...EMPTY_SECRETS, sse: v === "s3" ? draft.sse : "", pathStyle: v === "s3" ? draft.pathStyle : false })}
              />
              {draft.provider === "local" && (
                <div style={{ marginTop: 12 }}>
                  <Note tone="info" icon={<HardDrive size={15} />}>
                    New recordings stay on the control plane's volume. Recordings already copied to a bucket stay there
                    {saved?.hasCredential ? ", and the stored bucket credential is removed on save" : ""}.
                  </Note>
                </div>
              )}
            </SettingsField>

            {remote && (
              <>
                <SettingsField label="Bucket" required error={errors.bucket}>
                  <Input value={draft.bucket} onChange={(e) => set({ bucket: e.target.value })} placeholder="acme-ssh-recordings" style={inputMono} />
                </SettingsField>
                <SettingsField label="Prefix" hint="optional" error={errors.prefix}>
                  <Input value={draft.prefix} onChange={(e) => set({ prefix: e.target.value })} placeholder="authnull/recordings" style={inputMono} />
                </SettingsField>
                {draft.provider === "s3" && (
                  <SettingsField label="Region" hint="optional — defaults to us-east-1" error={errors.region}>
                    <Input value={draft.region} onChange={(e) => set({ region: e.target.value })} placeholder="us-east-1" style={inputMono} />
                  </SettingsField>
                )}
                <SettingsField label="Endpoint" hint={draft.provider === "s3" ? "optional — for MinIO, R2, Wasabi…" : "optional — custom endpoint"} error={errors.endpoint}>
                  <Input value={draft.endpoint} onChange={(e) => set({ endpoint: e.target.value })} placeholder={draft.provider === "s3" ? "https://minio.internal:9000" : "https://storage.googleapis.com"} style={inputMono} />
                </SettingsField>

                {draft.provider === "s3" && (
                  <>
                    <SettingsField label="Server-side encryption">
                      <Select value={draft.sse} options={SSE_OPTIONS} onChange={(v) => set({ sse: v, kmsKeyId: v === "aws:kms" ? draft.kmsKeyId : "" })} style={{ width: "100%" }} />
                    </SettingsField>
                    {draft.sse === "aws:kms" && (
                      <SettingsField label="KMS key ID" hint="optional — the AWS-managed key if empty" error={errors.kmsKeyId}>
                        <Input value={draft.kmsKeyId} onChange={(e) => set({ kmsKeyId: e.target.value })} placeholder="arn:aws:kms:…:key/…" style={inputMono} />
                      </SettingsField>
                    )}
                    <SettingsField label="Addressing">
                      <Checkbox checked={draft.pathStyle} onChange={(e) => set({ pathStyle: e.target.checked })}>
                        Path-style addressing <span style={{ color: AN_COLORS.muted }}>(most S3-compatible stores need this)</span>
                      </Checkbox>
                    </SettingsField>
                  </>
                )}
                {draft.provider === "gcs" && (
                  <SettingsField label="Customer-managed KMS key" hint="optional — Google-managed encryption if empty" error={errors.kmsKeyId}>
                    <Input value={draft.kmsKeyId} onChange={(e) => set({ kmsKeyId: e.target.value })} placeholder="projects/…/locations/…/keyRings/…/cryptoKeys/…" style={inputMono} />
                  </SettingsField>
                )}

                <SettingsField
                  label="Credentials"
                  error={errors.credentials}
                  help={draft.credentialMode === "ambient"
                    ? "Uses the control plane's own cloud identity (instance role, workload identity or environment). Nothing is stored."
                    : "Sealed in your organisation's vault. It is never shown again; leave the fields empty to keep the stored key."}
                >
                  <Segmented
                    options={[
                      { value: "ambient", label: "Service identity" },
                      { value: "static", label: "Stored key" },
                    ]}
                    value={draft.credentialMode}
                    onChange={(v) => set({ credentialMode: v, ...EMPTY_SECRETS })}
                  />
                  {draft.credentialMode === "static" && (storedUsable ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: AN_COLORS.ink2, marginTop: 12 }}>
                      <StoredTag /> A key is stored. Enter a new one only to replace it.
                    </div>
                  ) : saved?.hasCredential && providerChanged ? (
                    <div style={{ marginTop: 12 }}>
                      <Note tone="warn" icon={<AlertTriangle size={15} />}>
                        The stored key is for {PROVIDER_NAME[saved.provider]}; enter a {PROVIDER_NAME[draft.provider]} credential.
                      </Note>
                    </div>
                  ) : null)}
                </SettingsField>

                {draft.credentialMode === "static" && (draft.provider === "s3" ? (
                  <>
                    <SettingsField label="Access key ID" required={!storedUsable} error={errors.accessKeyId}>
                      <Input value={draft.accessKeyId} onChange={(e) => set({ accessKeyId: e.target.value })}
                        placeholder={storedUsable ? "•••••••• (stored)" : "AKIA…"} autoComplete="off" style={inputMono} />
                    </SettingsField>
                    <SettingsField label="Secret access key" required={!storedUsable} error={errors.secretAccessKey}>
                      <Input.Password value={draft.secretAccessKey} onChange={(e) => set({ secretAccessKey: e.target.value })}
                        placeholder={storedUsable ? "•••••••• (stored)" : ""} autoComplete="new-password" visibilityToggle={false} />
                    </SettingsField>
                    <SettingsField label="Session token" hint="optional — temporary credentials">
                      <Input.Password value={draft.sessionToken} onChange={(e) => set({ sessionToken: e.target.value })}
                        autoComplete="new-password" visibilityToggle={false} />
                    </SettingsField>
                  </>
                ) : (
                  <SettingsField label="Service account key (JSON)" required={!storedUsable} error={errors.serviceAccountJson}>
                    <Input.TextArea value={draft.serviceAccountJson} onChange={(e) => set({ serviceAccountJson: e.target.value })}
                      rows={5} placeholder={storedUsable ? "•••••••• (stored) — paste a new key file to replace it" : '{ "type": "service_account", "client_email": "…", "private_key": "…" }'}
                      style={{ fontFamily: AN_MONO, fontSize: 12 }} spellCheck={false} />
                  </SettingsField>
                ))}

                <SettingsField label="Existing recordings">
                  <Checkbox checked={draft.backfill} onChange={(e) => set({ backfill: e.target.checked })}>
                    Also move recordings already on the local volume
                    {recordings && recordings.local > 0 ? <span style={{ color: AN_COLORS.muted }}> (up to {recordings.local})</span> : null}
                  </Checkbox>
                </SettingsField>
              </>
            )}

            {saveError && (
              <CardBlock style={{ display: "grid", gap: 8 }}>
                <Note tone="danger" icon={<XCircle size={15} />}>
                  <strong>{saveError.status === 422 ? "Not saved." : "Could not save."}</strong> {saveError.error}
                </Note>
                {saveError.test?.warning && (
                  <Note tone="warn" icon={<AlertTriangle size={15} />}><strong>Note:</strong> {saveError.test.warning}</Note>
                )}
              </CardBlock>
            )}

            <CardBlock last style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, flexWrap: "wrap", background: AN_COLORS.bg }}>
              <span style={{ marginRight: "auto" }}>
                <SettingsStatus tone={dirty ? "warn" : "neutral"}>{dirty ? "Unsaved changes" : "No changes"}</SettingsStatus>
              </span>
              <Button disabled={!dirty || saving} onClick={() => { setDraft(draftFrom(saved)); setTouched(false); setSaveError(null); }}>
                Discard
              </Button>
              <Button type="primary" loading={saving} disabled={!dirty} onClick={save}>
                {saving && remote ? "Testing & saving…" : remote ? "Test & save" : "Save"}
              </Button>
            </CardBlock>
          </SettingsSection>
        </>
      )}
    </SettingsPage>
  );
}
